import { Icon, Stars } from "../../components/public/ui";
import { useAuthModal } from "../../components/public/auth/AuthModalContext";

const benefits = [
  { icon: "schedule", color: "text-primary", label: "Self-Paced Practice" },
  { icon: "verified", color: "text-tertiary", label: "CELPIP® Simulators" },
  {
    icon: "trending_up",
    color: "text-secondary",
    label: "Predictive Band Score",
  },
];

const mockOptions = [
  { label: "A. Announce membership price increases", selected: false },
  { label: "B. Inform about facility closure & alternatives", selected: true },
  { label: "C. Hire community recreation staff", selected: false },
];

export const Hero = () => {
  const { openAuth } = useAuthModal();

  return (
    <section
      id="top"
      className="relative w-full overflow-hidden bg-gradient-to-b from-surface via-surface to-surface-container-low/60 pt-8 pb-20 lg:py-24"
    >
      {/* Ambient blur background */}
      <div className="absolute -top-32 -left-20 w-96 h-96 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
      <div className="absolute top-1/3 -right-24 w-[30rem] h-[30rem] rounded-full bg-secondary-container/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-4 left-1/3 w-80 h-80 rounded-full bg-tertiary-fixed-dim/20 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left marketing column */}
          <div className="lg:col-span-6 flex flex-col items-start gap-6">
            <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-surface-container-highest/80 text-primary shadow-sm hover:shadow-md transition-all">
              <span className="w-2 h-2 rounded-full bg-tertiary animate-pulse" />
              <span className="font-label-sm text-label-sm uppercase tracking-wider text-primary">
                ✨ Your Smarter Path to English Test Success
              </span>
            </div>

            <h1 className="font-display-mobile text-display-mobile sm:font-display sm:text-display text-on-surface m-0">
              Prepare Smarter. Practice Better.
              <span className="block bg-gradient-to-r from-primary via-secondary-container to-tertiary bg-clip-text text-transparent">
                Pass Your English Test.
              </span>
            </h1>

            <p className="font-body-lg text-body-lg text-on-surface-variant max-w-xl m-0">
              Build unshakeable confidence and get exam-ready with realistic
              CELPIP practice tests, interactive exercises, and personalized
              feedback — all engineered in one intuitive platform.
            </p>

            <div className="flex flex-wrap items-center gap-4 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => openAuth("signup")}
                className="inline-flex items-center justify-center gap-2 font-label-lg text-label-lg px-7 py-4 rounded-xl bg-primary text-on-primary shadow-lg shadow-primary/25 hover:bg-primary-container transition-all hover:scale-[1.02] active:scale-[0.98]"
              >
                <span>Start Practicing Free</span>
                <Icon name="arrow_forward" className="text-lg" />
              </button>
              <a
                className="inline-flex items-center justify-center gap-2 font-label-lg text-label-lg px-6 py-4 rounded-xl bg-surface-container hover:bg-surface-container-high text-primary transition-all"
                href="#categories"
              >
                <span>Explore Practice Tests</span>
                <Icon name="explore" className="text-lg" />
              </a>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full pt-2">
              {benefits.map((b) => (
                <div
                  key={b.label}
                  className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-surface-container-lowest shadow-sm"
                >
                  <Icon name={b.icon} className={`${b.color} text-lg`} />
                  <span className="font-label-sm text-label-sm text-on-surface">
                    {b.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Micro social proof */}
            <div className="flex items-center gap-4 pt-2">
              <div className="flex -space-x-2.5">
                <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center text-on-primary font-bold text-xs ring-2 ring-surface">
                  AD
                </div>
                <div className="w-9 h-9 rounded-full bg-secondary-container flex items-center justify-center text-on-secondary font-bold text-xs ring-2 ring-surface">
                  CJ
                </div>
                <div className="w-9 h-9 rounded-full bg-tertiary flex items-center justify-center text-on-tertiary font-bold text-xs ring-2 ring-surface">
                  LT
                </div>
                <div className="w-9 h-9 rounded-full bg-primary-fixed text-primary flex items-center justify-center font-bold text-xs ring-2 ring-surface">
                  +
                </div>
              </div>
              <div className="flex flex-col">
                <div className="flex items-center gap-1 text-accent-warm">
                  <Stars />
                </div>
                <span className="font-body-sm text-body-sm text-on-surface-variant">
                  Trusted by CELPIP candidates preparing for Express Entry &amp;
                  PR
                </span>
              </div>
            </div>
          </div>

          {/* Right dashboard mockup */}
          <div className="lg:col-span-6 relative">
            <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-tertiary-fixed/30 rounded-3xl blur-2xl transform rotate-1 scale-95 -z-10" />

            <div className="w-full bg-surface-container-lowest rounded-2xl shadow-2xl overflow-hidden transition-all duration-300">
              {/* Browser header */}
              <div className="flex items-center justify-between px-5 py-3.5 bg-surface-container-high">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-error/70" />
                  <div className="w-3 h-3 rounded-full bg-accent-warm/70" />
                  <div className="w-3 h-3 rounded-full bg-tertiary/70" />
                  <span className="ml-3 font-label-sm text-label-sm text-on-surface-variant font-medium">
                    CELPIP General — Reading Test 3 of 8
                  </span>
                </div>
                <div className="flex items-center gap-2 bg-surface-container-lowest px-3 py-1 rounded-md">
                  <Icon name="timer" className="text-error text-sm" />
                  <span className="font-label-sm text-label-sm text-error font-bold tracking-tight">
                    Time: 08:42
                  </span>
                </div>
              </div>

              {/* Exam subhead */}
              <div className="px-6 py-2.5 bg-surface-container-low flex items-center justify-between text-xs text-on-surface-variant">
                <span className="font-label-sm text-label-sm font-semibold text-primary">
                  Part 1: Reading Correspondence
                </span>
                <div className="flex items-center gap-2">
                  <span className="font-label-sm text-label-sm">
                    Question 4 of 11
                  </span>
                  <div className="w-20 bg-surface-container-highest h-2 rounded-full overflow-hidden">
                    <div className="bg-primary h-full w-[36%] rounded-full" />
                  </div>
                </div>
              </div>

              {/* Mock exam body */}
              <div className="p-6 grid grid-cols-1 md:grid-cols-12 gap-5 text-left bg-surface-container-lowest">
                <div className="md:col-span-7 bg-surface-container-low p-4 rounded-xl flex flex-col gap-2.5 text-xs">
                  <div className="flex items-center justify-between pb-1.5">
                    <span className="font-label-sm text-label-sm text-primary font-bold">
                      Community Notice: Center Renovations
                    </span>
                    <span className="px-2 py-0.5 rounded bg-surface-container-highest text-on-surface-variant text-[10px]">
                      Reference Text
                    </span>
                  </div>
                  <p className="font-body-sm text-body-sm text-on-surface-variant leading-relaxed m-0">
                    Dear Residents of Maple Ridge, the civic recreation board
                    will commence modernization upgrades on April 14th. Primary
                    closures will affect the north multi-purpose sports hall and
                    adjacent family lockers.
                  </p>
                  <div className="p-2 rounded bg-tertiary-fixed/20 text-on-surface font-body-sm text-body-sm">
                    <span className="font-semibold text-tertiary">Notice:</span>{" "}
                    "Alternative fitness bookings will be honored at West Valley
                    Complex with zero transfer surcharges."
                  </div>
                  <p className="font-body-sm text-body-sm text-on-surface-variant m-0">
                    We kindly request patrons to clear out seasonal locker bays
                    before 6:00 PM on April 12th.
                  </p>
                </div>

                <div className="md:col-span-5 flex flex-col justify-between gap-3">
                  <div className="flex flex-col gap-2.5">
                    <span className="font-label-sm text-label-sm text-on-surface font-bold">
                      What is the main objective of this notice?
                    </span>
                    <div className="space-y-2">
                      {mockOptions.map((o) => (
                        <label
                          key={o.label}
                          className={
                            o.selected
                              ? "flex items-start gap-2.5 p-2 rounded-lg bg-primary-fixed/40 text-on-primary-fixed transition-colors cursor-pointer text-xs font-semibold"
                              : "flex items-start gap-2.5 p-2 rounded-lg bg-surface-container hover:bg-surface-container-high transition-colors cursor-pointer text-xs text-on-surface"
                          }
                        >
                          <input
                            className="mt-0.5 accent-primary"
                            name="hero-mock-q"
                            type="radio"
                            defaultChecked={o.selected}
                          />
                          <span>{o.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-2">
                    <button
                      type="button"
                      className="font-label-sm text-label-sm text-on-surface-variant flex items-center gap-1 hover:text-primary"
                    >
                      <Icon name="bookmark_border" className="text-sm" />
                      <span>Flag</span>
                    </button>
                    <button
                      type="button"
                      className="font-label-sm text-label-sm px-3 py-1.5 rounded-lg bg-primary text-on-primary flex items-center gap-1 hover:bg-primary-container"
                    >
                      <span>Next</span>
                      <Icon name="chevron_right" className="text-sm" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating pill: target band */}
            <div className="absolute -top-6 -right-4 sm:-right-6 bg-surface-container-lowest/90 backdrop-blur-md p-4 rounded-2xl shadow-xl flex items-center gap-3.5 transform hover:-translate-y-1 transition-transform">
              <div className="w-11 h-11 rounded-xl bg-tertiary-fixed flex items-center justify-center text-on-tertiary-fixed font-bold">
                <Icon name="award_star" className="text-xl" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-label-md text-label-md text-on-surface font-bold">
                    Target CLB 10
                  </span>
                  <span className="px-1.5 py-0.5 rounded text-[10px] bg-tertiary-fixed text-on-tertiary-fixed-variant font-bold">
                    +2 Bands
                  </span>
                </div>
                <p className="font-body-sm text-on-surface-variant text-xs m-0">
                  Estimated: 97th Percentile
                </p>
              </div>
            </div>

            {/* Floating pill: speaking fluency */}
            <div className="absolute -bottom-8 -left-4 sm:-left-6 bg-surface-container-lowest/90 backdrop-blur-md p-4 rounded-2xl shadow-xl flex items-center gap-4 transform hover:translate-y-1 transition-transform">
              <div className="w-12 h-12 rounded-xl bg-primary text-on-primary flex items-center justify-center">
                <Icon name="mic" className="text-xl" />
              </div>
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <span className="font-label-sm text-label-sm text-on-surface font-bold">
                    Speaking Fluency: 94%
                  </span>
                  <span className="w-2 h-2 rounded-full bg-tertiary animate-pulse" />
                </div>
                <div className="flex items-center gap-1 h-4">
                  <span className="w-1 h-2 bg-primary rounded-full animate-bounce" />
                  <span
                    className="w-1 h-4 bg-primary rounded-full animate-bounce"
                    style={{ animationDelay: "0.1s" }}
                  />
                  <span
                    className="w-1 h-3 bg-secondary rounded-full animate-bounce"
                    style={{ animationDelay: "0.2s" }}
                  />
                  <span
                    className="w-1 h-5 bg-tertiary rounded-full animate-bounce"
                    style={{ animationDelay: "0.3s" }}
                  />
                  <span className="w-1 h-2 bg-primary rounded-full" />
                  <span className="w-1 h-4 bg-primary rounded-full" />
                  <span className="font-label-sm text-[10px] text-on-surface-variant ml-1 font-semibold">
                    Native Cadence
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
